import { useRef } from "react";
import { Link } from "react-router-dom";
import { Flame } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { MENU } from "@/data/menu";

// Die 3 Bestseller mit echten Fotos
const BESTSELLER_IDS = ["p-offene", "g-taxi", "wu-curry-teller"];

const allDishes = Object.values(MENU).flat();
const dishes = BESTSELLER_IDS.map((id) => allDishes.find((d) => d.id === id)).filter(Boolean);

const fmtPrice = (n) => n.toFixed(2).replace(".", ",") + " €";

export default function MenuHighlights() {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <section
      id="speisekarte"
      ref={ref}
      data-testid="menu-section"
      className="relative bg-[var(--niko-cream-2)] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <p className="reveal divider-leaf max-w-[220px] mx-auto mb-8">
            Aus unserer Speisekarte
          </p>
          <h2
            className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--niko-charcoal)] leading-[1.05]"
            data-testid="menu-headline"
          >
            Unsere <span className="italic text-[var(--niko-blue)]">Bestseller</span>
          </h2>
          <p className="reveal reveal-delay-2 mt-5 text-base md:text-lg text-[var(--niko-charcoal)]/65">
            Die drei Gerichte, die unsere Gäste am meisten lieben.
          </p>
        </div>

        <div className="mt-16 md:mt-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {dishes.map((d, i) => (
            <Link
              to="/speisekarte"
              key={d.id}
              data-testid={`menu-card-${i}`}
              className={`reveal reveal-delay-${(i % 3) + 1} menu-card group block bg-white border border-[var(--niko-charcoal)]/8 rounded-sm overflow-hidden hover:border-[var(--niko-gold)]`}
            >
              <div className="relative aspect-[5/4] overflow-hidden bg-[var(--niko-cream-2)]">
                <img
                  src={d.photo}
                  alt={d.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--niko-gold)] text-[var(--niko-dark)] text-[10px] tracking-[0.14em] uppercase font-semibold">
                  <Flame className="w-3 h-3" />
                  Bestseller
                </span>
                <div className="absolute bottom-3 right-3 inline-flex items-center px-3 py-1.5 rounded-full bg-[var(--niko-dark)]/85 backdrop-blur text-[var(--niko-gold)] text-sm font-semibold tabular-nums">
                  {fmtPrice(d.price)}
                </div>
              </div>
              <div className="p-6 md:p-7">
                <h3 className="font-display text-2xl text-[var(--niko-charcoal)] leading-tight">
                  {d.name}
                </h3>
                <p className="mt-3 text-sm text-[var(--niko-charcoal)]/65 leading-relaxed line-clamp-3">
                  {d.desc}
                </p>
                <div className="mt-6 pt-5 border-t border-[var(--niko-charcoal)]/8 flex items-center justify-between">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-[var(--niko-charcoal)]/50">
                    Von Gästen geliebt
                  </span>
                  <span className="text-[var(--niko-gold)] text-sm font-medium opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                    Ansehen →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="reveal reveal-delay-3 mt-14 text-center">
          <Link
            to="/speisekarte"
            data-testid="menu-cta"
            className="inline-flex items-center gap-3 px-7 py-4 border border-[var(--niko-charcoal)] text-[var(--niko-charcoal)] text-sm font-semibold tracking-[0.18em] uppercase hover:bg-[var(--niko-charcoal)] hover:text-white transition-colors rounded-full"
          >
            Ganze Speisekarte ansehen
            <span>→</span>
          </Link>
          <p className="mt-5 text-xs text-[var(--niko-charcoal)]/55">
            Über 100 Gerichte & Getränke — klicken Sie sich durch unsere Karte.
          </p>
        </div>
      </div>
    </section>
  );
}
